import { ColorSimulationImageCreatedByType } from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'

/** 履歴のタイトルの上限文字数（サーバーのNormalizeHistoryTitle・DBのVARCHAR(30) と同じ。決定事項1） */
export const HISTORY_TITLE_MAX_LENGTH = 30

/** 1プロジェクトでピン留めできる履歴の上限（サーバーのAiColorPinLimitPerProjectと同じ。決定事項2） */
export const AI_COLOR_PIN_LIMIT = 4

export interface HistoryTitleSource {
  /** サーバーが返すタイトル。未設定は空文字列 */
  title?: string
  createdAt?: Date
  createdBy?: string
  createdByType?: ColorSimulationImageCreatedByType
}

export interface ResolvedHistoryTitle {
  /** 表示する文字列（タイトル、または未設定時の既定表示） */
  text: string
  /** 既定表示（タイトル未設定）か。細字・薄い色で描き「タイトル未設定」を添える */
  isDefault: boolean
}

/**
 * タイトル未設定時の既定表示を組み立てる（design.md D1）。
 *
 * - スタッフが作った履歴: 「年月日 氏名」（全角スペース区切り）
 * - お施主様が作った履歴: 年月日だけ（氏名は出さない）
 *
 * 日付は年あり・時刻なし（`YYYY/MM/DD`）。既定表示はお施主様にも見えるので、
 * 企業が遅い時間に作業した時刻を出さない（藤井確認済み）。年は同じ案を翌年に見返すことがあるので残す。
 */
export const getHistoryDefaultTitle = ({
  createdAt,
  createdBy,
  createdByType,
}: Omit<HistoryTitleSource, 'title'>): string => {
  const date = formatHistoryDate(createdAt)
  const isCustomerCreated = createdByType === ColorSimulationImageCreatedByType.CUSTOMER
  if (isCustomerCreated || !createdBy) {
    return date
  }
  return date ? `${date}\u3000${createdBy}` : createdBy
}

/** 既定表示に使う日付（`YYYY/MM/DD`。時刻は出さない）。undefinedなら空文字列 */
export const formatHistoryDate = (date: Date | undefined): string => {
  if (!date) {
    return ''
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}/${month}/${day}`
}

/** タイトルがあればそれを、無ければ既定表示を返す */
export const resolveHistoryTitle = (source: HistoryTitleSource): ResolvedHistoryTitle => {
  const title = source.title?.trim() ?? ''
  if (title) {
    return { text: title, isDefault: false }
  }
  return { text: getHistoryDefaultTitle(source), isDefault: true }
}

/** 文字数（コードポイント）。サーバーのrune数と同じ数え方 */
export const countHistoryTitleLength = (value: string): number => Array.from(value).length
